import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchStaffPermissions } from '../utils/staffPermissions';
import { persistor, store } from '../redux/store';
import {
  getStoredPermissions,
  shouldSyncPermissionsOnLoad,
} from '../utils/permissions';

const usePersistorBootstrapped = () => {
  const [bootstrapped, setBootstrapped] = useState(
    () => persistor.getState().bootstrapped
  );

  useEffect(() => {
    if (bootstrapped) return undefined;

    const unsubscribe = persistor.subscribe(() => {
      if (persistor.getState().bootstrapped) {
        setBootstrapped(true);
      }
    });

    if (persistor.getState().bootstrapped) {
      setBootstrapped(true);
    }

    return unsubscribe;
  }, [bootstrapped]);

  return bootstrapped;
};

/**
 * Keeps staff user permissions in sync with GET /rbac/my-permissions.
 * Tenant users do not see the sidebar until the latest API permissions are loaded.
 */
export function useStaffPermissions() {
  const user = useSelector((state) => state.user.data);
  const bootstrapped = usePersistorBootstrapped();
  const [permissionsReady, setPermissionsReady] = useState(false);
  const [syncedPermissions, setSyncedPermissions] = useState(null);
  const requestIdRef = useRef(0);

  const permissions = useMemo(() => {
    if (Array.isArray(syncedPermissions)) {
      return syncedPermissions;
    }

    return getStoredPermissions(user);
  }, [syncedPermissions, user]);

  const permissionSignature = useMemo(
    () => permissions.slice().sort().join('|'),
    [permissions]
  );

  const syncPermissions = useCallback(async ({ background = false } = {}) => {
    const currentUser = store.getState()?.user?.data;

    if (!currentUser?.token) {
      setPermissionsReady(false);
      setSyncedPermissions(null);
      return null;
    }

    const mustSync = shouldSyncPermissionsOnLoad(currentUser);

    if (!mustSync) {
      setSyncedPermissions(getStoredPermissions(currentUser));
      setPermissionsReady(true);
      return getStoredPermissions(currentUser);
    }

    if (!background) {
      setPermissionsReady(false);
    }

    const requestId = ++requestIdRef.current;

    try {
      const nextPermissions = await fetchStaffPermissions(currentUser.token);

      if (requestId !== requestIdRef.current) {
        return null;
      }

      if (Array.isArray(nextPermissions)) {
        setSyncedPermissions(nextPermissions);
        setPermissionsReady(true);
        return nextPermissions;
      }

      const fallback = getStoredPermissions(currentUser);
      setSyncedPermissions(fallback);
      setPermissionsReady(true);
      return fallback;
    } catch (error) {
      console.error('Failed to sync permissions:', error);

      if (requestId !== requestIdRef.current) {
        return null;
      }

      const fallback = getStoredPermissions(currentUser);
      setSyncedPermissions(fallback);
      setPermissionsReady(true);
      return fallback;
    }
  }, []);

  useEffect(() => {
    if (!bootstrapped) return undefined;

    setSyncedPermissions(null);
    syncPermissions();

    return () => {
      requestIdRef.current += 1;
    };
  }, [
    bootstrapped,
    syncPermissions,
    user?.token,
    user?.user?.user_data?.auth_type,
    user?.user?.user_data?.tenant_user_id,
    user?.user?.user_data?.role_id,
  ]);

  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        syncPermissions({ background: true });
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [syncPermissions]);

  return {
    user,
    permissions,
    permissionSignature,
    permissionsReady: bootstrapped && permissionsReady,
    refreshPermissions: () => syncPermissions({ background: true }),
  };
}
